import api from '../api/client';
// components/SaleReversalModal.jsx
import React, { useState, useEffect, use } from 'react';
import { Modal, Form, Input, Button, message, Table, Tag, Descriptions, Space, Alert } from 'antd';
import { ExclamationCircleOutlined, RollbackOutlined } from '@ant-design/icons';
import axios from 'axios';
import { formatKES } from '../utils/currency';
import dayjs from 'dayjs';
import config from '../config';
import { useSelector } from 'react-redux';

const { TextArea } = Input;

export default function SaleReversalModal({ visible, saleId, onClose, onSuccess }) {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [saleData, setSaleData] = useState(null);
    const account = useSelector((state) => state.account.user);
    const [form] = Form.useForm();
    const environment = window.location.hostname === 'localhost' ? 'development' : 'production';
        const apiUrl = config[environment].apiUrl;
    
    useEffect(() => {
        if (visible && saleId) {
            fetchSaleDetails();
        }
    }, [visible, saleId]);
    
    const fetchSaleDetails = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/sale/${saleId}`);
            setSaleData(response.data.data);
        } catch (error) {
            console.error('Error fetching sale details:', error);
            message.error('Failed to load sale details');
        } finally {
            setLoading(false);
        }
    };
    
    const handleReversal = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);
            
            const response = await api.post(`/reverse-sale/${saleId}`, {
                reason: values.reason,
                userId: account.id, // Replace with actual user ID
                userName: account.firstname // Replace with actual user name
            });
            
            if (response.data.success) {
                message.success('Sale reversed successfully');
                onSuccess && onSuccess();
                onClose();
            }
        } catch (error) {
            console.error('Error reversing sale:', error);
            message.error(error.response?.data?.message || 'Failed to reverse sale');
        } finally {
            setSubmitting(false);
        }
    };
    
    const columns = [
        { 
            title: 'Item', 
            dataIndex: 'productName', 
            key: 'productName',
            render: (_, record) => {
                if (record.itemType === 'egg') {
                    return `${record.category || 'Normal'} Eggs - Batch: ${record.identifier}`;
                }
                return record.identifier || record.productName || 'Product';
            }
        },
        { 
            title: 'Quantity', 
            dataIndex: 'quantity', 
            key: 'quantity',
            render: (qty, record) => `${qty} ${record.unit || 'units'}`
        },
        { 
            title: 'Unit Price', 
            dataIndex: 'unitPrice', 
            key: 'unitPrice',
            render: (price) => formatKES(price)
        },
        { 
            title: 'Total', 
            dataIndex: 'totalPrice', 
            key: 'totalPrice',
            render: (total) => formatKES(total)
        }
    ];
    
    if (loading) {
        return (
            <Modal
                title="Reverse Sale"
                open={visible}
                onCancel={onClose}
                footer={null}
                width={800}
            >
                <div style={{ textAlign: 'center', padding: 40 }}>Loading sale details...</div>
            </Modal>
        );
    }
    
    if (!saleData) return null;
    
    return (
        <Modal
            title={
                <Space>
                    <RollbackOutlined style={{ color: '#ff4d4f' }} />
                    <span>Reverse Sale</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button 
                    key="reverse" 
                    type="primary" 
                    danger 
                    loading={submitting} 
                    onClick={handleReversal}
                >
                    Confirm Reversal
                </Button>
            ]}
            width={800}
        >
            <Alert
                message="Warning: This action cannot be undone"
                description="Reversing a sale will restore all inventory items and reverse accounting entries. This action is permanent and will be logged."
                type="warning"
                showIcon
                icon={<ExclamationCircleOutlined />}
                style={{ marginBottom: 16 }}
            />
            
            <Descriptions title="Sale Information" bordered size="small" column={2}>
                <Descriptions.Item label="Sale ID">{saleData.sale.id}</Descriptions.Item>
                <Descriptions.Item label="Transaction ID">{saleData.sale.transactionId}</Descriptions.Item>
                <Descriptions.Item label="Customer">{saleData.sale.stakeholder_name || `ID: ${saleData.sale.stakeholderId}`}</Descriptions.Item>
                <Descriptions.Item label="Date">{dayjs(saleData.sale.recorded).format('YYYY-MM-DD')}</Descriptions.Item>
                <Descriptions.Item label="Total Amount" span={2}>
                    <Tag color="red">{formatKES(saleData.sale.totalCost)}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Payment Status" span={2}>
                    <Tag color={saleData.sale.paymentStatus === 'paid' ? 'green' : 'orange'}>
                        {saleData.sale.paymentStatus}
                    </Tag>
                </Descriptions.Item>
            </Descriptions>
            
            <div style={{ margin: '16px 0' }}>
                <strong>Items to be reversed:</strong>
            </div>
            <Table
                dataSource={saleData.items}
                columns={columns}
                rowKey="id"
                pagination={false}
                size="small"
            />
            
            <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                <Form.Item
                    name="reason"
                    label="Reversal Reason"
                    rules={[{ required: true, message: 'Please provide a reason for reversal' }]}
                >
                    <TextArea rows={3} placeholder="Enter reason for reversing this sale..." />
                </Form.Item>
            </Form>
        </Modal>
    );
}